import React, { Fragment } from 'react';
import Link from 'shared/SlashLink';
import usePageSeo from '../../hooks/usePageSeo';
import Header from '../../components/header/Header';
import PageTitle from '../../components/pagetitle/PageTitle';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Footer from '../../components/footer/Footer';
import CtaSection from '../../components/CtaSection/CtaSection';
import { BOOKING_URL, BOOKING_ENABLED } from '../../config/booking';
import { trackEvent } from '../../utils/analytics';

const ClickHandler = () => window.scrollTo(10, 0);

// Recommended anchor price — confirm/replace with your real number.
const PRICE = '$1,995';

const INCLUDED = [
    { t: 'Starter website', d: 'A clean, mobile-ready 3–5 page site built on your brand, ready to launch.' },
    { t: 'Custom logo design', d: 'An original logo with full file set for web and print.' },
    { t: 'Domain name (URL)', d: 'We register and configure your .com (or preferred domain).' },
    { t: 'Web hosting — 1 year', d: 'Fast, secure hosting included for your first year.' },
    { t: '5 business email accounts', d: 'Professional name@yourdomain.com inboxes for your team.' },
    { t: 'Social media setup', d: 'Profile photo, banner, and description set up on LinkedIn, Facebook & Instagram.' },
];

const ADDONS = [
    { t: 'Business phone number', d: 'A local or toll-free line for your business.' },
    { t: 'Social media content & management', d: 'Ongoing posts and account management so your profiles stay active.' },
    { t: 'Marketing & SEO planning + tracking', d: 'A keyword and content plan with analytics tracking to grow traffic.' },
    { t: 'Additional website pages', d: 'Extra pages as your business grows.' },
    { t: 'Online store or booking', d: 'Add e-commerce or appointment scheduling.' },
    { t: 'Extra email & branded signatures', d: 'More inboxes plus polished, on-brand email signatures.' },
    { t: 'Google Business Profile setup', d: 'Create and optimize your Google listing for local search.' },
];

const StartupPackagePage = () => {
    usePageSeo({
        title: 'Small Business Startup Package — Website, Logo, Domain, Email & Social',
        description: 'Get your new California business online fast — starter website, custom logo, domain, hosting, 5 business email accounts, and social media setup in one bundle.',
        keywords: 'small business startup package, new business website bundle, logo and website package, domain hosting email bundle, social media setup, startup web design California, BUILDLAB',
    });

    return (
        <Fragment>
            <Header />
            <main id="main-content" className="page_content">
                <PageTitle pageTitle={'Small Business Startup Package'} pagesub={'Bundle'} pageTop={'Web & Branding'} />
                <section className="section_space bg-light">
                    <div className="container">
                        <div className="row mb-5">
                            <div className="col-lg-7">
                                <h2 className="mb-3">Everything a new business needs to launch — in one package</h2>
                                <p>
                                    Starting a business is enough work without juggling five vendors for your website, logo, domain,
                                    email, and social accounts. BUILDLAB bundles it all into one done-for-you startup package so you
                                    can open for business online — fast, on-brand, and ready to grow.
                                </p>
                            </div>
                        </div>

                        <div className="row">
                            {/* Included */}
                            <div className="col-lg-7 mb-4">
                                <h3 className="mb-3">Everything included</h3>
                                <div className="row">
                                    {INCLUDED.map((it) => (
                                        <div className="col-md-6 mb-3" key={it.t}>
                                            <div style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 14, padding: '18px 20px', height: '100%' }}>
                                                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                                    <span style={{ flex: '0 0 auto', width: 26, height: 26, borderRadius: '50%', background: 'var(--bs-primary)', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>
                                                        <i className="fa-solid fa-check" aria-hidden="true"></i>
                                                    </span>
                                                    <div>
                                                        <div style={{ fontWeight: 700, color: 'var(--bs-dark)', marginBottom: 2 }}>{it.t}</div>
                                                        <div style={{ fontSize: '.86rem' }}>{it.d}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Price card */}
                            <div className="col-lg-5 mb-4">
                                <div style={{ background: 'var(--bs-dark)', borderRadius: 16, padding: '30px 32px', color: '#fff', position: 'sticky', top: 100 }}>
                                    <div style={{ fontSize: '.85rem', opacity: 0.8 }}>Startup Package</div>
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '4px 0 2px' }}>
                                        <span style={{ fontSize: '3rem', fontWeight: 800, lineHeight: 1 }}>{PRICE}</span>
                                        <span style={{ fontSize: '1rem', opacity: 0.75 }}>one-time</span>
                                    </div>
                                    <p style={{ fontSize: '.86rem', opacity: 0.85, marginTop: 8 }}>
                                        Website, logo, domain, one year of hosting, 5 email accounts, and social setup on LinkedIn,
                                        Facebook &amp; Instagram — all done for you.
                                    </p>
                                    <p style={{ fontSize: '.78rem', opacity: 0.7 }}>
                                        Domain &amp; hosting renew at standard rates after year one. Add-ons priced on request.
                                    </p>
                                    <Link to="/contact" onClick={ClickHandler} className="btn btn-primary w-100 mt-2" style={{ display: 'block', textAlign: 'center' }}>
                                        <span className="btn_label" data-text="Start my business online">Start my business online</span>
                                    </Link>
                                    {BOOKING_ENABLED && (
                                        <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('book_consult', { tool: 'Startup Package' })} style={{ display: 'block', textAlign: 'center', marginTop: 12, color: '#FFC9A3', fontWeight: 600, fontSize: '.9rem', textDecoration: 'none' }}>
                                            Book a free 15-min consult <i className="fa-solid fa-arrow-up-right" aria-hidden="true" style={{ fontSize: '.72rem' }}></i>
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Add-ons */}
                        <div className="row mt-2">
                            <div className="col-lg-12">
                                <h3 className="mb-1">Grow when you’re ready — available add-ons</h3>
                                <p className="text-muted mb-4" style={{ fontSize: '.92rem' }}>Bolt these on at signup or later. Priced on request based on your needs.</p>
                                <div className="row">
                                    {ADDONS.map((a) => (
                                        <div className="col-md-6 col-lg-4 mb-3" key={a.t}>
                                            <div style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 14, padding: '18px 20px', height: '100%' }}>
                                                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                                    <span style={{ flex: '0 0 auto', width: 26, height: 26, borderRadius: 8, background: 'var(--bs-primary-bg-subtle)', color: 'var(--bs-primary)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>
                                                        <i className="fa-solid fa-plus" aria-hidden="true"></i>
                                                    </span>
                                                    <div>
                                                        <div style={{ fontWeight: 700, color: 'var(--bs-dark)', marginBottom: 2 }}>{a.t}</div>
                                                        <div style={{ fontSize: '.84rem' }}>{a.d}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <p className="small text-muted mt-3" style={{ maxWidth: 880 }}>
                            Timelines depend on how quickly you can provide content (text, photos, brand preferences). Serving small
                            businesses across California. Already have a logo or domain? We’ll adjust the package and price accordingly.
                        </p>
                    </div>
                </section>
                <CtaSection />
            </main>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default StartupPackagePage;
